import { buildApiUrl } from "../utils/apiConfig";
import { jsonGet, jsonSend } from "../utils/http";

export interface NilDealDto {
    id: number;
    dealName: string;
    description: string;
    dateSubmitted: string; // ISO date (YYYY-MM-DD)
    hasAgreement: boolean;
    agreementFileName: string | null;
}

export interface NilDealPage {
    content: NilDealDto[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

export interface NilDealPayload {
    dealName: string;
    description: string;
    dateSubmitted: string; // ISO date
}

// User-friendly messages surfaced to the modal. The backend already returns friendly `detail`
// text for 400s (validation, unsupported type, oversized, failed security scan); these cover
// transport-level and unexpected failures so the user never sees a raw technical error.
const MSG_NETWORK =
    "We couldn’t reach the server. Please check your internet connection and try again. If this keeps happening, contact Polaris support.";
const MSG_TOO_LARGE = "That file is too large. Please choose a file up to 15 MB.";
const MSG_DENIED =
    "We couldn’t save this deal because the request was denied. Please refresh and try again.";
const MSG_GENERIC = "Something went wrong while saving. Please try again.";

function withAthlete(params: URLSearchParams, athleteUserId?: number | null): void {
    if (athleteUserId != null) {
        params.set("athleteUserId", String(athleteUserId));
    }
}

function athleteSuffix(athleteUserId?: number | null): string {
    const params = new URLSearchParams();
    withAthlete(params, athleteUserId);
    const q = params.toString();
    return q ? `?${q}` : "";
}

function buildDealFormData(payload: NilDealPayload, file: File | null): FormData {
    const fd = new FormData();
    fd.append("dealName", payload.dealName);
    fd.append("description", payload.description);
    fd.append("dateSubmitted", payload.dateSubmitted);
    if (file) {
        fd.append("agreement", file, file.name);
    }
    return fd;
}

function uploadErrorFor(status: number, detail?: string | null): Error {
    if (status === 413) {
        return new Error(MSG_TOO_LARGE);
    }
    if (status === 401 || status === 403) {
        return new Error(MSG_DENIED);
    }
    // 400s carry a friendly, user-facing detail from the backend (e.g. failed security scan).
    return new Error((detail && detail.trim()) || MSG_GENERIC);
}

/**
 * Sends a multipart create/update to the Polaris API. The optional agreement file is streamed to
 * our server (where it is virus scanned before it is ever stored in S3), and upload progress is
 * reported via `onProgress` so the modal can show a progress bar for large documents.
 */
function sendDealMultipart<T>(
    url: string,
    method: "POST" | "PUT",
    formData: FormData,
    onProgress?: (pct: number) => void
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url);
        xhr.withCredentials = true; // send the auth cookie
        if (onProgress) {
            xhr.upload.onprogress = (ev) => {
                if (ev.lengthComputable) {
                    onProgress(Math.round((ev.loaded / ev.total) * 100));
                }
            };
        }
        xhr.onload = () => {
            let body: any = null;
            try {
                body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch {
                /* non-JSON body */
            }
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(body as T);
                return;
            }
            reject(uploadErrorFor(xhr.status, body?.detail || body?.message));
        };
        xhr.onerror = () => reject(new Error(MSG_NETWORK));
        xhr.send(formData);
    });
}

export function fetchNilDeals(
    pageZeroBased: number,
    pageSize: number,
    athleteUserId?: number | null
): Promise<NilDealPage> {
    const params = new URLSearchParams();
    params.set("page", String(pageZeroBased));
    params.set("size", String(pageSize));
    withAthlete(params, athleteUserId);
    return jsonGet<NilDealPage>(`${buildApiUrl("api/nil-deals")}?${params.toString()}`);
}

export function createNilDeal(
    payload: NilDealPayload,
    file: File | null,
    athleteUserId?: number | null,
    onProgress?: (pct: number) => void
): Promise<NilDealDto> {
    const url = buildApiUrl(`api/nil-deals${athleteSuffix(athleteUserId)}`);
    return sendDealMultipart<NilDealDto>(url, "POST", buildDealFormData(payload, file), onProgress);
}

export function updateNilDeal(
    dealId: number,
    payload: NilDealPayload,
    file: File | null,
    athleteUserId?: number | null,
    onProgress?: (pct: number) => void
): Promise<NilDealDto> {
    const url = buildApiUrl(`api/nil-deals/${dealId}${athleteSuffix(athleteUserId)}`);
    return sendDealMultipart<NilDealDto>(url, "PUT", buildDealFormData(payload, file), onProgress);
}

export function getNilDealAgreementUrl(
    dealId: number,
    athleteUserId?: number | null
): Promise<{ url: string }> {
    const params = new URLSearchParams();
    withAthlete(params, athleteUserId);
    const q = params.toString();
    const suffix = q ? `?${q}` : "";
    return jsonGet<{ url: string }>(buildApiUrl(`api/nil-deals/${dealId}/agreement-url${suffix}`));
}

export function deleteNilDeal(
    dealId: number,
    athleteUserId?: number | null
): Promise<void> {
    const params = new URLSearchParams();
    withAthlete(params, athleteUserId);
    const q = params.toString();
    const suffix = q ? `?${q}` : "";
    return jsonSend<void>(buildApiUrl(`api/nil-deals/${dealId}${suffix}`), "DELETE");
}
